import { customElement, property } from "lit/decorators.js";
import { UserAnnotation, UserAnnotationFormData, userAnnotationState } from "@/state/UserAnnotations";
import { html, TemplateResult } from "lit";
import { submissionState } from "@/state/Submissions";
import { AnnotationForm } from "@/components/annotations/annotation_form";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { annotationState } from "@/state/Annotations";
import { evaluationState } from "@/state/Evaluations";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { userState } from "@/state/Users";

/**
 * This component represents a thread of annotations.
 * It also contains the form for creating new annotations.
 *
 * @element d-thread
 *
 * @prop {number} rootId - The id of the root annotation for this thread
 * @prop {boolean} formShown - Whether the form to reply is shown (Internal)
 */
@customElement("d-thread")
export class Thread extends DodonaElement {
    @property({ type: Number, attribute: "root-id" })
    rootId: number;

    @property({ state: true })
    formShown = false;

    annotationFormRef: Ref<AnnotationForm> = createRef();

    get data(): UserAnnotation {
        return userAnnotationState.byId.get(this.rootId);
    }

    get openQuestions(): UserAnnotation[] {
        return [this.data, ...this.data.responses].filter(
            (response) => response.question_state !== undefined && response.question_state !== "answered",
        );
    }

    get isUnanswered(): boolean {
        return this.openQuestions.length > 0;
    }

    get fakeInputPlaceholder(): string {
        if (this.firstResponse() && this.data.draft_answer_id) {
            return `✨${I18n.t("js.user_annotation.reply")}...`;
        }
        return `${I18n.t("js.user_annotation.reply")}...`;
    }

    async createAnnotation(e: CustomEvent): Promise<void> {
        const annotationData: UserAnnotationFormData = {
            annotation_text: e.detail.text,
            line_nr: this.data.line_nr,
            evaluation_id: evaluationState.id,
            saved_annotation_id: e.detail.savedAnnotationId || undefined,
            thread_root_id: this.data.id,
        };

        try {
            const mode = annotationState.isQuestionMode ? "question" : "annotation";
            await userAnnotationState.create(
                annotationData,
                submissionState.id,
                mode,
                e.detail.saveAnnotation,
                e.detail.savedAnnotationTitle,
            );
            this.formShown = false;
        } catch {
            const form = this.annotationFormRef.value;
            form.hasErrors = true;
            form.disabled = false;
        }
    }

    markAsResolved(): void {
        void userAnnotationState.transitionAll(this.openQuestions, "answered");
    }

    markAsInProgress(): void {
        void userAnnotationState.transitionAll(
            this.openQuestions.filter((question) => question.question_state !== "in_progress"),
            "in_progress",
        );
    }

    markAsUnanswered(): void {
        void userAnnotationState.transitionAll(
            this.openQuestions.filter((question) => question.question_state !== "unanswered"),
            "unanswered",
        );
    }

    addReply(): void {
        this.formShown = true;
        this.markAsInProgress();
    }

    cancelReply(): void {
        this.formShown = false;
        this.markAsUnanswered();
    }

    firstResponse(): boolean {
        return !this.data.responses.some((response) => response.user_id !== this.data.user_id);
    }

    get canCreateAnnotation(): boolean {
        return userState.hasPermission("annotation.create");
    }

    onThreadMouseEnter(): void {
        this.data.isHovered = true;
    }

    onThreadMouseLeave(): void {
        this.data.isHovered = false;
    }

    onFormSubmit(e: CustomEvent): void {
        void this.createAnnotation(e);
    }

    onFormCancel(): void {
        this.cancelReply();
    }

    onAddReplyClick(): void {
        this.addReply();
    }

    onFakeInputKeyDown(e: KeyboardEvent): void {
        // The "fake input" is meant to open the reply form.
        // Make it work for keyboard users too.
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.addReply();
        }
    }

    onResolveClick(): void {
        this.markAsResolved();
    }

    get form(): TemplateResult {
        const draftId = this.firstResponse() ? this.data.draft_answer_id : undefined;

        return this.formShown
            ? html`
                  <div class="annotation ${annotationState.isQuestionMode ? "question" : "user"}">
                      <d-annotation-form
                          @submit=${this.onFormSubmit}
                          ${ref(this.annotationFormRef)}
                          @cancel=${this.onFormCancel}
                          draft-id=${draftId ?? undefined}
                          submit-button-text="reply"
                      ></d-annotation-form>
                  </div>
              `
            : html`
                  <div class="fake-input">
                      <input
                          type="text"
                          class="form-control grayscale"
                          placeholder=${this.fakeInputPlaceholder}
                          readonly
                          aria-readonly="true"
                          aria-label=${this.fakeInputPlaceholder}
                          @click=${this.onAddReplyClick}
                          @keydown=${this.onFakeInputKeyDown}
                      />
                      ${
                          this.isUnanswered
                              ? html`
                                    <span>${I18n.t("js.user_question.or")}</span>
                                    <button type="button" class="btn btn-text" @click=${this.onResolveClick}>
                                        ${I18n.t("js.user_question.resolve")}
                                    </button>
                                `
                              : html``
                      }
                  </div>
              `;
    }

    render(): TemplateResult {
        return this.data
            ? html`
                  <div
                      class="thread ${annotationState.isVisible(this.data) ? "" : "hidden"}"
                      @mouseenter=${this.onThreadMouseEnter}
                      @mouseleave=${this.onThreadMouseLeave}
                  >
                      <d-user-annotation .data=${this.data}></d-user-annotation>
                      ${this.data.responses.map(
                          (response) => html` <d-user-annotation .data=${response}></d-user-annotation> `,
                      )}
                      ${this.canCreateAnnotation ? this.form : html``}
                  </div>
              `
            : html``;
    }
}
